import React, { useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  variant?: 'centered' | 'bottom-sheet';
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[95vw] max-h-[95dvh]',
};

// Bottom-sheet desktop fallback: aplica max-w só em lg:+ (mobile fica w-full)
const lgSizeClasses = {
  sm: 'lg:max-w-md',
  md: 'lg:max-w-lg',
  lg: 'lg:max-w-2xl',
  xl: 'lg:max-w-4xl',
  full: 'lg:max-w-[95vw] lg:max-h-[95dvh]',
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  variant = 'centered',
}) => {
  const titleId = useId();
  const isBottomSheet = variant === 'bottom-sheet';

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (closeOnEscape && event.key === 'Escape') {
        onClose();
      }
    },
    [closeOnEscape, onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const outerClasses = isBottomSheet
    ? 'fixed inset-0 z-50 flex items-end justify-center lg:items-center lg:p-4'
    : 'fixed inset-0 z-50 flex items-center justify-center p-4';

  const contentClasses = isBottomSheet
    ? `relative w-full ${lgSizeClasses[size]} max-h-[90dvh] pb-[env(safe-area-inset-bottom)] liquid-glass rounded-t-2xl rounded-b-none overflow-hidden shadow-2xl animate-slide-up lg:max-h-[unset] lg:rounded-xl lg:animate-reveal-zoom`
    : `relative w-full ${sizeClasses[size]} liquid-glass rounded-xl overflow-hidden shadow-2xl animate-reveal-zoom`;

  return createPortal(
    <div className={outerClasses}>
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
        onClick={closeOnOverlayClick ? onClose : undefined}
      />

      {/* Modal Content */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? 'Modal' : undefined}
        className={contentClasses}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-700/50">
            {title && (
              <h2 id={titleId} className="text-lg font-semibold text-white">{title}</h2>
            )}
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Fechar modal"
                className="p-3 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors ml-auto"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className={`px-4 py-3 overflow-y-auto ${size === 'xl' || size === 'full' ? 'max-h-[85dvh]' : 'max-h-[70dvh]'}`}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};
